import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { EmailProviderSetupContract, EmailShareProvider } from '@/contracts/share'
import { AlertTriangle, KeyRound } from 'lucide-react'

type EmailProviderSetupHintProps = {
  provider: EmailShareProvider
  providerSetups: EmailProviderSetupContract[]
  onProviderChange: (provider: EmailShareProvider) => void
}

export function EmailProviderSetupHint({
  provider,
  providerSetups,
  onProviderChange,
}: EmailProviderSetupHintProps) {
  const selectedSetup =
    providerSetups.find((setup) => setup.provider === provider) ?? providerSetups[0]
  if (!selectedSetup) return null

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
      <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 size-5 shrink-0 text-amber-600" />
          <div>
            <div className="font-semibold text-amber-950">寄信 provider 尚未啟用</div>
            <p className="mt-1 text-sm leading-6 text-amber-900">
              目前只做 Email payload 與範本預覽，不會寄出真信。正式啟用前需要環境變數、 kill
              switch、rate limit、audit log 與安全審查。
            </p>
          </div>
        </div>
        <div className="w-full xl:w-60">
          <Select
            value={provider}
            onValueChange={(value) => onProviderChange(value as EmailShareProvider)}
          >
            <SelectTrigger className="bg-white" aria-label="選擇寄信 provider 規劃模式">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {providerSetups.map((setup) => (
                <SelectItem key={setup.provider} value={setup.provider}>
                  {setup.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-4 grid gap-3 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="rounded-lg border border-amber-200 bg-white p-3">
          <div className="text-sm font-semibold text-slate-950">{selectedSetup.label}</div>
          <p className="mt-2 text-sm leading-6 text-slate-600">{selectedSetup.recommendedUse}</p>
          <p className="mt-2 text-sm leading-6 text-amber-800">{selectedSetup.warning}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Badge variant="warning">{selectedSetup.status}</Badge>
            <Badge variant="outline">mock-safe</Badge>
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border border-amber-200 bg-white">
          <div className="border-b border-amber-100 px-3 py-2 text-sm font-semibold text-slate-950">
            需要設定的環境變數
          </div>
          <div className="max-h-72 overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>變數</TableHead>
                  <TableHead>用途</TableHead>
                  <TableHead>範例</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {selectedSetup.envVars.map((envVar) => (
                  <TableRow key={`${selectedSetup.provider}-${envVar.key}`}>
                    <TableCell className="whitespace-nowrap font-mono text-xs">
                      <span className="inline-flex items-center gap-1">
                        {envVar.secret ? <KeyRound className="size-3 text-amber-600" /> : null}
                        {envVar.key}
                      </span>
                    </TableCell>
                    <TableCell className="min-w-52 text-xs text-slate-600">
                      {envVar.description}
                      <div className="mt-1 text-[11px] text-slate-600">{envVar.location}</div>
                    </TableCell>
                    <TableCell className="min-w-40 font-mono text-xs text-slate-500">
                      {envVar.example}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  )
}
